import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useTheme,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Snackbar,
  alpha,
  Avatar,
  LinearProgress
} from '@mui/material';
import { Email as EmailIcon, Warning as WarningIcon, WarningAmber as WarningAmberIcon, Send as SendIcon, Person as PersonIcon, Print as PrintIcon, School as SchoolIcon } from '@mui/icons-material';
import axios from 'axios';
import SecretarySidebar from '../../components/SecretarySidebar';

function getCurrentSchoolYear() {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  if (month >= 9) {
    return `${year}-${year + 1}`;
  } else {
    return `${year - 1}-${year}`;
  }
}

function getSchoolYears(count = 5) {
  const now = new Date();
  const currentYear = now.getMonth() >= 7 ? now.getFullYear() : now.getFullYear() - 1;
  const includeNextYear = now.getMonth() >= 4;
  const start = includeNextYear ? currentYear + 1 : currentYear;
  const length = includeNextYear ? count + 1 : count;
  return Array.from({ length: length }, (_, i) => {
    const y = start - (length - 1 - i);
    return `${y}-${y + 1}`;
  }).reverse();
}

const Arrears = () => {
  const theme = useTheme();
  const [schoolYear, setSchoolYear] = useState(getCurrentSchoolYear());
  const [availableYears] = useState<string[]>(getSchoolYears(5));
  const [arrears, setArrears] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState<{ [key: number]: boolean }>({});
  const [selectedClass, setSelectedClass] = useState<string>('Toutes');
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  useEffect(() => {
    fetchArrears();
  }, [schoolYear]);

  const fetchArrears = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://lapetiteacademie.ci/api/payments/arrears?school_year=${schoolYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArrears(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erreur lors de la récupération des retards.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminder = async (studentId: number) => {
    setSending({ ...sending, [studentId]: true });
    try {
      const token = localStorage.getItem('token');
      await axios.post('https://lapetiteacademie.ci/api/payments/remind', {
        studentId,
        school_year: schoolYear
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSnackbarMessage('Relance envoyée avec succès');
    } catch (err: any) {
      setSnackbarMessage(err.response?.data?.message || "Erreur lors de l'envoi de la relance");
    } finally {
      setSending({ ...sending, [studentId]: false });
    }
  };

  const getOverdueTotal = (scheduleItems: any[]) => {
    return scheduleItems
      .filter(item => item.status === 'overdue')
      .reduce((sum, item) => sum + item.remaining, 0);
  };

  const arrearsByClass = arrears.reduce((acc, curr) => {
    const className = curr.student.className || 'Non assigné';
    if (!acc[className]) {
      acc[className] = [];
    }
    acc[className].push(curr);
    return acc;
  }, {} as Record<string, any[]>);

  return (
    <Box sx={{ display: { xs: 'flex', print: 'block' }, minHeight: { xs: '100vh', print: 'auto' }, bgcolor: 'grey.50' }}>
      <Box sx={{ '@media print': { display: 'none' } }}>
        <SecretarySidebar />
      </Box>
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 3, print: 0 }, width: '100%' }}>
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', '@media print': { display: 'none' } }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel id="school-year-label">Année scolaire</InputLabel>
            <Select
              labelId="school-year-label"
              value={schoolYear}
              label="Année scolaire"
              onChange={e => setSchoolYear(e.target.value)}
            >
              {availableYears.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button 
            variant="outlined" 
            color="primary" 
            startIcon={<PrintIcon />} 
            onClick={() => window.print()}
          >
            Imprimer la liste
          </Button>
        </Box>

        <Paper 
          elevation={0}
          sx={{
            mb: 4,
            p: 3,
            background: `linear-gradient(135deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)`,
            color: 'white',
            borderRadius: 3,
            '@media print': { display: 'none' }
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon sx={{ fontSize: 40, mr: 2 }} />
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
                Suivi des Retards et Relances
              </Typography>
              <Typography variant="body1" sx={{ opacity: 0.9 }}>
                Consultez les versements non soldés dont la date d'échéance est dépassée.
              </Typography>
            </Box>
          </Box>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 3, boxShadow: 3, '@media print': { display: 'none' } }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : arrears.length === 0 ? (
            <Alert severity="success">Aucun élève en retard pour cette année scolaire.</Alert>
          ) : (
            <Box>
              <Box sx={{ mb: 4, display: 'flex', gap: 1, flexWrap: 'wrap', '@media print': { display: 'none' } }}>
                <Chip 
                  label="Toutes les classes" 
                  onClick={() => setSelectedClass('Toutes')}
                  color={selectedClass === 'Toutes' ? 'primary' : 'default'}
                  variant={selectedClass === 'Toutes' ? 'filled' : 'outlined'}
                  sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 2 }}
                />
                {Object.keys(arrearsByClass).sort().map(c => (
                  <Chip 
                    key={c}
                    label={`${c} (${arrearsByClass[c].length})`}
                    onClick={() => setSelectedClass(c)}
                    color={selectedClass === c ? 'primary' : 'default'}
                    variant={selectedClass === c ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 'bold', fontSize: '0.9rem', py: 2 }}
                  />
                ))}
              </Box>
              {Object.keys(arrearsByClass).sort().filter(c => selectedClass === 'Toutes' || c === selectedClass).map(className => (
                <Box key={className} sx={{ mb: 6 }}>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: 'text.secondary', mb: 2, display: 'flex', alignItems: 'center' }}>
                    <SchoolIcon sx={{ mr: 1 }} /> Classe : {className}
                    <Chip label={`${arrearsByClass[className].length} élève(s)`} size="small" sx={{ ml: 2, fontWeight: 'bold' }} />
                  </Typography>
                  <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                    <Table sx={{ minWidth: 800 }}>
                      <TableHead>
                        <TableRow sx={{ background: `linear-gradient(90deg, ${theme.palette.error.main} 0%, ${theme.palette.error.dark} 100%)` }}>
                          <TableCell sx={{ color: 'white', fontWeight: 700, py: 2, minWidth: 200 }}>Élève</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 700, py: 2, textAlign: 'center' }}>Inscription</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 700, py: 2, textAlign: 'center' }}>1er Vers.</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 700, py: 2, textAlign: 'center' }}>2ème Vers.</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 700, py: 2, textAlign: 'center' }}>3ème Vers.</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 700, py: 2, textAlign: 'center' }}>4ème Vers.</TableCell>
                          <TableCell sx={{ color: 'white', fontWeight: 700, py: 2, textAlign: 'center', minWidth: 120 }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {arrearsByClass[className].map((arr: any, idx: number) => {
                          const totalOverdue = getOverdueTotal(arr.schedule);
                          return (
                          <TableRow 
                            key={idx} 
                            sx={{ 
                              '&:last-child td, &:last-child th': { border: 0 },
                              '&:hover': { backgroundColor: alpha(theme.palette.error.main, 0.04) },
                              transition: 'background-color 0.2s ease'
                            }}
                          >
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                                <Avatar sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', mr: 2, width: 40, height: 40, mt: 0.5 }}>
                                  <PersonIcon />
                                </Avatar>
                                <Box>
                                  <Typography sx={{ fontWeight: 800, color: 'text.primary' }}>{arr.student.firstName} {arr.student.lastName}</Typography>
                                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: arr.student.parentEmail ? 'text.secondary' : 'text.disabled' }}>
                                    {arr.student.parentEmail || 'Email non défini'}
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>
                            
                            {arr.schedule.map((item: any, i: number) => {
                               const isPaid = item.status === 'paid';
                               const isOverdue = item.status === 'overdue';
                               const isPartial = item.status === 'partial';
                               const progress = item.expected > 0 ? Math.min(100, Math.round((item.paid / item.expected) * 100)) : 0;
                               
                               return (
                                 <TableCell key={i} align="center" sx={{ borderLeft: `1px dashed ${alpha(theme.palette.divider, 0.6)}`, bgcolor: isOverdue ? alpha(theme.palette.error.main, 0.02) : 'transparent' }}>
                                   {isPaid ? (
                                     <Chip label="Soldé" sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.dark', fontWeight: 'bold' }} size="small" />
                                   ) : (
                                     <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', minWidth: 80 }}>
                                       <Typography variant="caption" sx={{ color: isOverdue ? 'error.main' : 'text.primary', fontWeight: 800, fontSize: '0.75rem' }}>
                                         {item.remaining.toLocaleString()} F
                                       </Typography>
                                       <LinearProgress 
                                         variant="determinate" 
                                         value={progress} 
                                         sx={{ 
                                           width: '80%', 
                                           mt: 0.5, 
                                           mb: 0.5, 
                                           height: 4, 
                                           borderRadius: 2,
                                           bgcolor: isOverdue ? alpha(theme.palette.error.main, 0.1) : alpha(theme.palette.primary.main, 0.1),
                                           '& .MuiLinearProgress-bar': { bgcolor: isOverdue ? 'error.main' : 'primary.main' }
                                         }} 
                                       />
                                       <Chip 
                                         label={isOverdue ? 'En retard' : isPartial ? 'Partiel' : 'En attente'} 
                                         size="small" 
                                         sx={{ 
                                           height: 18, 
                                           fontSize: '0.6rem', 
                                           fontWeight: 'bold',
                                           bgcolor: isOverdue ? alpha(theme.palette.error.main, 0.1) : isPartial ? alpha(theme.palette.warning.main, 0.1) : alpha(theme.palette.grey[500], 0.1),
                                           color: isOverdue ? 'error.dark' : isPartial ? 'warning.dark' : 'text.secondary'
                                         }} 
                                       />
                                     </Box>
                                   )}
                                 </TableCell>
                               );
                            })}

                            <TableCell align="center" sx={{ borderLeft: `1px solid ${alpha(theme.palette.divider, 0.5)}` }}>
                              <Typography variant="subtitle2" sx={{ color: 'error.main', fontWeight: 900, mb: 1 }}>
                                Total: {totalOverdue.toLocaleString()} F
                              </Typography>
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                sx={{ borderRadius: 8, textTransform: 'none', fontWeight: 'bold', boxShadow: `0 4px 14px 0 ${alpha(theme.palette.error.main, 0.39)}` }}
                                endIcon={sending[arr.student.id] ? <CircularProgress size={14} color="inherit" /> : <SendIcon sx={{ fontSize: 16 }} />}
                                disabled={sending[arr.student.id] || !arr.student.parentEmail}
                                onClick={() => handleSendReminder(arr.student.id)}
                              >
                                Relancer
                              </Button>
                            </TableCell>
                          </TableRow>
                        )})}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </Box>
          )}
        </Paper>

        {/* --- VUE IMPRESSION NORMALISÉE --- */}
        <style>
          {`
            @media print {
              @page { size: landscape; margin: 10mm; }
            }
          `}
        </style>
        <Box sx={{ display: 'none', '@media print': { display: 'block' } }}>
          <Typography variant="h5" sx={{ mb: 2, textAlign: 'center', fontWeight: 'bold' }}>
            Liste des Retards de Paiement - {schoolYear}
          </Typography>
          {Object.keys(arrearsByClass).sort().filter(c => selectedClass === 'Toutes' || c === selectedClass).map(className => (
            <Box key={className} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 1, fontWeight: 'bold', borderBottom: '2px solid black' }}>
                Classe : {className}
              </Typography>
              <table style={{ width: '100%', borderCollapse: 'collapse', border: '1px solid black' }}>
                <thead>
                  <tr style={{ backgroundColor: '#f2f2f2' }}>
                    <th style={{ border: '1px solid black', padding: '8px', textAlign: 'left', whiteSpace: 'nowrap' }}>Élève</th>
                    <th style={{ border: '1px solid black', padding: '8px', textAlign: 'left' }}>Contact Parent</th>
                    <th style={{ border: '1px solid black', padding: '8px' }}>Inscription</th>
                    <th style={{ border: '1px solid black', padding: '8px' }}>1er Vers.</th>
                    <th style={{ border: '1px solid black', padding: '8px' }}>2ème Vers.</th>
                    <th style={{ border: '1px solid black', padding: '8px' }}>3ème Vers.</th>
                    <th style={{ border: '1px solid black', padding: '8px' }}>4ème Vers.</th>
                    <th style={{ border: '1px solid black', padding: '8px' }}>Total Retard</th>
                  </tr>
                </thead>
                <tbody>
                  {arrearsByClass[className].map((arr: any, idx: number) => {
                    const totalOverdue = getOverdueTotal(arr.schedule);
                    return (
                      <tr key={idx} style={{ pageBreakInside: 'avoid' }}>
                        <td style={{ border: '1px solid black', padding: '8px', whiteSpace: 'nowrap' }}>{arr.student.firstName} {arr.student.lastName}</td>
                        <td style={{ border: '1px solid black', padding: '8px' }}>{arr.student.parentEmail || 'N/A'}</td>
                        {arr.schedule.map((item: any, i: number) => {
                           const text = item.status === 'paid' ? 'Soldé' : `Reste: ${item.remaining.toLocaleString()}`;
                           return (
                             <td key={i} style={{ border: '1px solid black', padding: '8px', textAlign: 'center', color: item.status === 'overdue' ? 'red' : 'black', fontWeight: item.status === 'overdue' ? 'bold' : 'normal' }}>
                               {text}
                             </td>
                           );
                        })}
                        <td style={{ border: '1px solid black', padding: '8px', fontWeight: 'bold', color: 'red', textAlign: 'center' }}>
                          {totalOverdue.toLocaleString()} F
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </Box>
          ))}
        </Box>
        {/* --- FIN VUE IMPRESSION --- */}

      </Box>

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default Arrears;
